import { ReplacePsycoWithLifestyleOptions, TaxonomyItem } from './types';

const isEmptyLabel = (label: string): boolean => label === '';

export const filterOut = <T extends TaxonomyItem>(
  level: T[],
  list: string[],
  prop: keyof TaxonomyItem = 'id'
): T[] => level.filter((item) => !list.includes(String(item[prop])));

export const replacePsycoWithLifestyle = <T extends TaxonomyItem>(
  level: T[],
  options: ReplacePsycoWithLifestyleOptions = {}
): T[] => {
  const {
    label = 'Lifestyles',
    description = 'Lifestyles',
    psychographicType = 'psychographic',
    omitDescription = false,
  } = options;

  return level.map((item) => {
    if (item.id !== 'psychographic') {
      return item;
    }

    return {
      ...item,
      id: 'lifestyle',
      label,
      type: psychographicType,
      ...(omitDescription ? {} : { description }),
    };
  });
};

/** Renames root psychographic → lifestyle for WW legacy flattening (enriched pipeline). */
export const getLevelWithLifestyle = <T extends TaxonomyItem>(
  level: T[]
): T[] =>
  level.map((item) => {
    if (item.id !== 'psychographic') {
      return item;
    }

    return {
      ...item,
      id: 'lifestyle',
      label: 'Lifestyles',
      description: 'Lifestyles',
    } as T;
  });

export const getLevelWithoutPsycoSubs = <T extends TaxonomyItem>(
  level: T[]
): T[] => level.filter((item) => item.parent_id !== 'psychographic');

export const getLevelWithRootOverview = <T extends TaxonomyItem>(
  level: T[]
): TaxonomyItem[] => [
  {
    id: 'root',
    label: 'Overview',
    isSummary: true,
  },
  ...level,
];

export const getParentIdFromChild = (
  taxonomy: TaxonomyItem[],
  itemId: string
): string | undefined => {
  if (!taxonomy || taxonomy.length === 0) {
    return undefined;
  }

  const found = taxonomy.find((item) => item.id === itemId);

  if (found?.parent_id && found.parent_id !== itemId) {
    return getParentIdFromChild(taxonomy, found.parent_id);
  }

  return found?.id;
};

export const getTaxonomyPathFromItem = (
  taxonomy: TaxonomyItem[],
  itemId: string,
  label = ''
): string => {
  if (!taxonomy || taxonomy.length === 0) {
    return label;
  }

  const found = taxonomy.find((item) => item.id === itemId);

  if (found?.parent_id && found.parent_id !== itemId) {
    const newLabel = isEmptyLabel(label)
      ? found.label ?? ''
      : `${found.label} > ${label}`;
    return getTaxonomyPathFromItem(taxonomy, found.parent_id, newLabel);
  }

  if (found) {
    return isEmptyLabel(label)
      ? found.label ?? ''
      : `${found.label} > ${label}`;
  }

  return label;
};

export const getTaxonomyPaths = (
  taxonomy: TaxonomyItem[]
): Record<string, string> =>
  taxonomy.reduce<Record<string, string>>((acc, item) => {
    acc[item.id] = getTaxonomyPathFromItem(taxonomy, item.id);
    return acc;
  }, {});
